import React from 'react';
import PropTypes from 'prop-types';
import {
  alpha,
  Box,
  CircularProgress,
  IconButton,
  Stack,
  Tooltip,
  Typography,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import FileDownloadOutlinedIcon from '@mui/icons-material/FileDownloadOutlined';
import { C } from '../tokens';
import { prettySector } from '../formatters';

// Sticky header at the top of the breakout detail drawer.
const DetailDrawerHeaderBar = ({
  detailDrawer,
  isCompact,
  drawerExporting,
  onExport,
  onClose,
  t,
  language,
}) => (
  <Box
    sx={{
      position: 'sticky',
      top: 0,
      zIndex: 2,
      borderBottom: `1px solid ${C.border}`,
      bgcolor: '#fff',
      px: { xs: 1.5, sm: 2.25 },
      py: { xs: 1.1, sm: 1.5 },
    }}
  >
    {isCompact ? (
      <Box sx={{ width: 34, height: 4, borderRadius: 99, bgcolor: '#cbd5e1', mx: 'auto', mb: 1 }} />
    ) : null}

    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1.25 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, minWidth: 0, flex: 1 }}>
        <Box sx={{
          width: 48,
          height: 48,
          borderRadius: 0.75,
          overflow: 'hidden',
          boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
        }}>
          <Box sx={{ flex: 1, bgcolor: '#1e293b', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontWeight: 900, color: '#fff', fontSize: '0.88rem', lineHeight: 1, letterSpacing: '0.02em' }}>
              {detailDrawer?.ticker || '—'}
            </Typography>
          </Box>
          <Box sx={{ height: 12, bgcolor: '#7c3aed', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
            <Typography sx={{ fontWeight: 800, color: '#fff', fontSize: '0.5rem', letterSpacing: '0.12em', lineHeight: 1 }}>
              BRK
            </Typography>
          </Box>
        </Box>

        <Box sx={{ minWidth: 0, flex: 1 }}>
          <Typography
            sx={{
              fontWeight: 800,
              fontSize: { xs: '1rem', sm: '1rem' },
              color: C.ink,
              lineHeight: 1.15,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
              minWidth: 0,
            }}
          >
            {detailDrawer?.company_name || prettySector(detailDrawer?.sector, language)}
          </Typography>
          {detailDrawer ? (
            <Stack direction="row" spacing={0.6} useFlexGap flexWrap="wrap" sx={{ mt: 0.35 }}>
              {detailDrawer?.sector ? (
                <Typography sx={{
                  fontSize: '0.62rem',
                  fontWeight: 700,
                  color: '#0284c7',
                  bgcolor: alpha('#0284c7', 0.08),
                  px: 0.7,
                  py: 0.1,
                  borderRadius: 0.8,
                }}>
                  {prettySector(detailDrawer.sector, language)}
                </Typography>
              ) : null}
              {detailDrawer?.score_date ? (
                <Typography sx={{ fontSize: '0.66rem', color: '#64748b' }}>
                  {detailDrawer.score_date}
                </Typography>
              ) : null}
            </Stack>
          ) : null}
        </Box>
      </Box>

      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }} data-html2canvas-ignore="true">
        <Tooltip title={t('vsa.detail.export')} placement="bottom">
          <span>
            <IconButton
              size="small"
              onClick={onExport}
              disabled={!detailDrawer || drawerExporting}
              sx={{ color: '#475569' }}
            >
              {drawerExporting ? (
                <CircularProgress size={18} color="inherit" />
              ) : (
                <FileDownloadOutlinedIcon sx={{ fontSize: 20 }} />
              )}
            </IconButton>
          </span>
        </Tooltip>
        <IconButton size="small" onClick={onClose} sx={{ color: '#475569' }}>
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>
    </Box>
  </Box>
);

DetailDrawerHeaderBar.propTypes = {
  detailDrawer: PropTypes.object, // eslint-disable-line react/forbid-prop-types
  isCompact: PropTypes.bool,
  drawerExporting: PropTypes.bool,
  onExport: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  language: PropTypes.string,
};

DetailDrawerHeaderBar.defaultProps = {
  detailDrawer: null,
  isCompact: false,
  drawerExporting: false,
  language: 'en',
};

export default DetailDrawerHeaderBar;
